'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';
import { EkinEntry, generateCSV, downloadCSV, BULAN_LIST, TAHUN_LIST } from '@/lib/csv-utils';
import { 
  Moon, Sun, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  FileText, User, BarChart3, AlertCircle, Loader2, Sparkles, Send, Plus, Download,
  Heart, X, RefreshCw, Cpu, FileEdit, Trash2, ClipboardList, Settings, DownloadCloud
} from 'lucide-react';
import GitImportModal from '@/components/GitImportModal';

import ThemeToggle from '@/components/ThemeToggle';

// Loading spinner
function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '48px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        border: '3px solid var(--border)',
        borderTopColor: 'var(--accent)',
        animation: 'spin-slow 1s linear infinite',
      }} />
      <div>
        <p style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>Sedang memproses dengan AI...</p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Menganalisis commit dan menghasilkan catatan kinerja</p>
      </div>
    </div>
  );
}

// Toast component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span>{type === "success" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}</span>
      {message}
    </div>
  );
}

// Searchable Select component (like Select2)
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Cari...',
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset highlight when search changes
  useEffect(() => { setHighlightIdx(0); }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlightIdx]) {
      e.preventDefault();
      onChange(filtered[highlightIdx].value);
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Display selected or search input */}
      <div
        className="input-field"
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        style={{
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', minHeight: 44, gap: 8,
        }}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              flex: 1, fontSize: 13, color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          />
        ) : (
          <span style={{
            flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          }}>
            {selectedLabel || '-- Pilih Rencana Kinerja --'}
          </span>
        )}
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>
          {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          marginTop: 4, background: 'var(--bg-primary)',
          border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: 220, overflowY: 'auto', zIndex: 50,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              Tidak ditemukan
            </div>
          ) : (
            filtered.map((opt, idx) => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                style={{
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  lineHeight: 1.5,
                  background: idx === highlightIdx
                    ? 'rgba(99, 102, 241, 0.1)'
                    : opt.value === value
                      ? 'rgba(99, 102, 241, 0.05)'
                      : 'transparent',
                  color: opt.value === value ? 'var(--accent)' : 'var(--text-primary)',
                  fontWeight: opt.value === value ? 600 : 400,
                  borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Normalize time string to HH:mm format for <input type="time">
// Handles: "8:00", "8:00:00", "08:00:00", "08:00", "7:30"
function normalizeTime(time: string | undefined): string {
  if (!time) return '08:00';
  // Remove seconds if present (e.g. "08:00:00" -> "08:00")
  const parts = time.split(':');
  const hours = parts[0]?.padStart(2, '0') || '00';
  const minutes = parts[1]?.padStart(2, '0') || '00';
  return `${hours}:${minutes}`;
}

export default function Home() {
  const { data: session, status } = useSession({ required: true });

  // Auto re-login when refresh token fails
  useEffect(() => {
    if ((session as any)?.error === 'RefreshAccessTokenError') {
      signIn('keycloak');
    }
  }, [session]);

  // Form state
  const [commitLogs, setCommitLogs] = useState('');
  const [isGitModalOpen, setIsGitModalOpen] = useState(false);
  const [isCheckingTokens, setIsCheckingTokens] = useState(false);
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);
  const [showMissingTokenModal, setShowMissingTokenModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [bulan, setBulan] = useState(() => BULAN_LIST[new Date().getMonth()]);
  const [tahun, setTahun] = useState(() => new Date().getFullYear().toString());
  const [rencana, setRencana] = useState('');
  const [minPerHari, setMinPerHari] = useState('2');
  const [maxPerHari, setMaxPerHari] = useState('2');
  const [tanggalMulai, setTanggalMulai] = useState('');
  const [tanggalSelesai, setTanggalSelesai] = useState('');

  // State
  const [entries, setEntries] = useState<EkinEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Biodata state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [biodata, setBiodata] = useState<any>(null);
  const [biodataLoading, setBiodataLoading] = useState(false);

  // Rencana kinerja state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rencanaList, setRencanaList] = useState<any[]>([]);
  const [rencanaLoading, setRencanaLoading] = useState(false);

  // SKP / Atasan state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [skpData, setSkpData] = useState<any>(null);

  // Fetch biodata + SKP atasan when session is available
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nip = (session as any)?.nip;
    if (session && nip) {
      setBiodataLoading(true);
      Promise.all([
        fetch(`/api/pegawai/${nip}`).then(r => r.json()),
        fetch(`/api/skp-tahunan/${nip}`).then(r => r.json()).catch(() => []),
      ])
        .then(([pegawaiRes, skpRes]) => {
          setBiodata(pegawaiRes.data || pegawaiRes);
          // SKP data: array sorted by date, first item = latest/current
          if (Array.isArray(skpRes) && skpRes.length > 0) {
            setSkpData(skpRes[0]);
          }
        })
        .catch(() => setBiodata(null))
        .finally(() => setBiodataLoading(false));
    }
  }, [session]);

  // Fetch rencana kinerja when bulan/tahun changes
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nip = (session as any)?.nip;
    if (session && nip && bulan && tahun) {
      setRencanaLoading(true);
      setRencana('');
      fetch(`/api/rencana/${nip}/${bulan}/${tahun}`)
        .then(res => res.json())
        .then(res => {
          const list = res?.data?.rencanaKinerja || res?.rencanaKinerja || [];
          setRencanaList(list);
          if (list.length === 1) {
            setRencana(list[0].targetSkpBkn);
          }
        })
        .catch(() => setRencanaList([]))
        .finally(() => setRencanaLoading(false));
    }
  }, [session, bulan, tahun]);

  // File upload handler
  const handleFileUpload = useCallback((file: File) => {
    if (!file.name.endsWith('.txt')) {
      setToast({ message: 'File harus berformat .txt', type: 'error' });
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCommitLogs(text);
      // Auto-detect project name from file name
      const baseName = file.name.replace('.txt', '');
      if (!projectName) setProjectName(baseName);
    };
    reader.readAsText(file);
  }, [projectName]);

  // Open Git modal handler
  const handleOpenGitModal = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nip = (session as any)?.nip;
    if (!nip) {
      setToast({ message: 'Silakan login terlebih dahulu', type: 'error' });
      return;
    }
    
    setIsCheckingTokens(true);
    try {
        const res = await fetch(`/api/settings?nip=${nip}`);
        const { data } = await res.json();
        
        const platforms = [];
        if (data?.githubToken) platforms.push('github');
        if (data?.gitlabToken) platforms.push('gitlab');
        
        if (platforms.length === 0) {
            setShowMissingTokenModal(true);
        } else {
            setAvailablePlatforms(platforms);
            setIsGitModalOpen(true);
        }
    } catch (e) {
        setToast({ message: 'Gagal mengecek token', type: 'error' });
    } finally {
        setIsCheckingTokens(false);
    }
  };

  // Drop zone handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  // Generate
  const handleGenerate = async () => {
    if (!commitLogs.trim()) {
      setToast({ message: 'Masukkan commit logs', type: 'error' });
      return;
    }
    if (!rencana.trim()) {
      setToast({ message: 'Masukkan rencana kinerja', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commitLogs, projectName, bulan, tahun, rencana, minPerHari: parseInt(minPerHari), maxPerHari: parseInt(maxPerHari), tanggalMulai, tanggalSelesai }),
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({ message: data.error || 'Gagal generate', type: 'error' });
        return;
      }

      // Memastikan tiap baris dari AI kita set default selected
      setEntries(data.entries.map((e: any) => ({ ...e, waktuMulai: normalizeTime(e.waktuMulai), waktuSelesai: normalizeTime(e.waktuSelesai), isSelected: true })));
      setToast({ message: `Berhasil generate ${data.entries.length} catatan kinerja`, type: 'success' });
    } catch {
      setToast({ message: 'Gagal terhubung ke server', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit entry
  const updateEntry = (index: number, field: keyof EkinEntry, value: any) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Delete entry
  const deleteEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  // Add entry
  const addEntry = () => {
    setEntries((prev) => [
      ...prev,
      { tanggal: '', rencana: rencana, uraian: '', jumlah: '1', satuan: 'Kegiatan', waktuMulai: '8:00', waktuSelesai: '10:00', isSelected: true },
    ]);
  };

  // Export CSV
  const handleExportCSV = () => {
    const selectedEntries = entries.filter((e) => e.isSelected !== false);
    if (selectedEntries.length === 0) {
      setToast({ message: 'Tidak ada data yang dicentang untuk di-export', type: 'error' });
      return;
    }
    const csv = generateCSV(selectedEntries);
    const filename = `${bulan}-${tahun}.csv`;
    downloadCSV(csv, filename);
    setToast({ message: `File ${filename} berhasil didownload`, type: 'success' });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Failed entries state for retry
  interface FailedEntry {
    entry: EkinEntry;
    error: string;
    rencanaKinerjaId: number;
  }
  const [failedEntries, setFailedEntries] = useState<FailedEntry[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleSubmitEkinerja = async () => {
    if (!biodata) {
      setToast({ message: 'Biodata belum termuat', type: 'error' });
      return;
    }
    const selectedEntries = entries.filter((e) => e.isSelected !== false);
    if (selectedEntries.length === 0) {
      setToast({ message: 'Pilih minimal satu entri untuk dikirim', type: 'error' });
      return;
    }

    const selectedRencana = rencanaList.find(r => r.targetSkpBkn === rencana);
    const rencanaKinerjaId = selectedRencana?.id;

    if (!rencanaKinerjaId) {
      setToast({ message: 'Rencana Kinerja tidak valid atau belum dipilih', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    let successCount = 0;
    const newFailedEntries: typeof failedEntries = [];

    try {
      for (const entry of selectedEntries) {
        // Calculate jam & menit from waktuMulai/waktuSelesai
        const wMulai = entry.waktuMulai || '08:00';
        const wSelesai = entry.waktuSelesai || '16:00';
        const [hM, mM] = wMulai.split(':').map(Number);
        const [hS, mS] = wSelesai.split(':').map(Number);
        let totalMenit = (hS * 60 + mS) - (hM * 60 + mM);
        if (totalMenit < 0) totalMenit = 0;
        const jam = Math.floor(totalMenit / 60);
        const menit = totalMenit % 60;
        const waktuStr = jam > 0
          ? (menit > 0 ? `${jam} Jam ${menit} Menit` : `${jam} Jam 0 Menit`)
          : `${menit} Menit`;

        const payload = {
          tanggal: entry.tanggal,
          waktuMulai: `${wMulai}:00`,
          waktuSelesai: `${wSelesai}:00`,
          jam,
          menit,
          waktu: waktuStr,
          uraian: entry.uraian,
          output: entry.satuan,
          skp: "ada",
          nip: biodata.nip || biodata.id_pegawai,
          foto: biodata.photo || biodata.foto,
          nama: biodata.nama?.nama || biodata.nama,
          jab: biodata.jab?.nama || biodata.nomenklatur_jabatan,
          atasan: skpData?.pejabatPenilaiNip || biodata.atasan || biodata.nip_atasan_langsung,
          atasanNama: skpData?.pejabatPenilaiNama || biodata.atasanNama?.nama || biodata.atasanNama || biodata.nama_atasan_langsung,
          atasanJab: skpData?.pejabatPenilaiJab || biodata.atasanJab?.nama || biodata.atasanJab || biodata.jabatan_atasan_langsung,
          skpd: biodata.unit?.id || biodata.skpd || biodata.id_skpd,
          skpdNama: biodata.unit?.nama || biodata.skpdNama || biodata.nama_skpd,
          subUnit: biodata.subunit?.id || biodata.subUnit || biodata.id_sub_unit,
          jumlah: Number(entry.jumlah),
          rencanaKinerjaId: rencanaKinerjaId
        };

        try {
          const res = await fetch('/api/submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (res.ok) {
            const resData = await res.json().catch(() => null);
            if (resData?.success === false || resData?.error) {
              newFailedEntries.push({
                entry,
                error: resData?.error || resData?.details?.message || 'Unknown error',
                rencanaKinerjaId,
              });
            } else {
              successCount++;
            }
          } else {
            const errData = await res.json().catch(() => null);
            newFailedEntries.push({
              entry,
              error: errData?.error || errData?.details?.message || `HTTP ${res.status}`,
              rencanaKinerjaId,
            });
          }
        } catch {
          newFailedEntries.push({
            entry,
            error: 'Network error',
            rencanaKinerjaId,
          });
        }
      }

      setFailedEntries(newFailedEntries);

      if (newFailedEntries.length === 0) {
        setToast({ message: `Berhasil mengirim ${successCount} entri ke E-Kinerja`, type: 'success' });
      } else if (successCount === 0) {
        setToast({ message: `Gagal mengirim semua ${newFailedEntries.length} entri`, type: 'error' });
      } else {
        setToast({ message: `${successCount} berhasil, ${newFailedEntries.length} gagal`, type: 'error' });
      }
    } catch (e) {
      setToast({ message: 'Gagal mengirim data ke E-Kinerja', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count commit lines
  const commitCount = commitLogs.split('\n').filter((l) => l.trim().length > 0).length;

  // Loading state while checking auth
  if (status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '3px solid var(--border)', borderTopColor: 'var(--accent)',
          animation: 'spin-slow 1s linear infinite',
        }} />
        <p style={{ marginTop: 16, fontSize: 14, color: 'var(--text-muted)' }}>Memuat sesi...</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', zIndex: 1, maxWidth: 1600, margin: '0 auto', padding: '24px 40px 80px' }}>
      {/* Header */}
      <header className="animate-fade-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'var(--accent-gradient)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}><FileEdit size={20} /></div>
            <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em' }}>EKIN Generator</h1>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginLeft: 52 }}>
            Generate catatan kinerja harian dari git commits menggunakan AI
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {session?.user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                padding: '6px 14px', borderRadius: 10,
                background: 'rgba(99, 102, 241, 0.08)',
                fontSize: 13, fontWeight: 500, color: 'var(--accent)',
              }}>
                <User size={14} style={{ marginRight: 6 }} /> {session.user.name || 'User'}
              </div>
              <a
                href="/rencana-kinerja"
                className="btn-secondary"
                style={{ padding: '8px 14px', fontSize: 12, textDecoration: 'none' }}
              >
                <ClipboardList size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} /> Rencana Kinerja
              </a>
              <a
                href="/laporan-triwulan"
                className="btn-secondary"
                style={{ padding: '8px 14px', fontSize: 12, textDecoration: 'none' }}
              >
                <BarChart3 size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} /> Laporan Triwulan
              </a>
              <a
                href="/settings"
                className="btn-secondary"
                style={{ padding: '8px 14px', fontSize: 12, textDecoration: 'none' }}
              >
                <Settings size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} /> Pengaturan
              </a>
              <button
                onClick={async () => {
                  await signOut({ redirect: false });
                  window.location.href = '/login';
                }}
                className="btn-secondary"
                style={{ padding: '8px 14px', fontSize: 12 }}
              >
                Logout
              </button>
            </div>
          )}
          <ThemeToggle />
        </div>
      </header>

      {/* Biodata Card */}
      <div className="glass-card animate-slide-up" style={{ padding: 24, marginBottom: 20 }}>
        {biodataLoading ? (
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <div className="loading-shimmer" style={{ width: 72, height: 72, borderRadius: 16 }} />
            <div style={{ flex: 1 }}>
              <div className="loading-shimmer" style={{ height: 18, width: 250, borderRadius: 6, marginBottom: 8 }} />
              <div className="loading-shimmer" style={{ height: 14, width: 200, borderRadius: 6, marginBottom: 8 }} />
              <div className="loading-shimmer" style={{ height: 12, width: 320, borderRadius: 6 }} />
            </div>
          </div>
        ) : biodata ? (
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Photo */}
            <div style={{
              width: 80, height: 100, borderRadius: 14, overflow: 'hidden',
              flexShrink: 0, border: '2px solid var(--border)',
              background: 'var(--bg-secondary)',
            }}>
              {biodata.photo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={`https://e-kepegawaian.purbalinggakab.go.id/packages/upload/photo/pegawai/${biodata.photo}`}
                  alt={biodata.nama}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--accent-gradient)', color: 'white',
                  fontSize: 28, fontWeight: 700,
                }}>
                  {(biodata.nama || 'U').charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 240 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 2 }}>
                {biodata.nama}{biodata.gdp ? `, ${biodata.gdp}` : ''}{biodata.gdb ? `, ${biodata.gdb}` : ''}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, marginBottom: 10 }}>
                {biodata.jab?.nama}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 32px' }}>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>NIP</span>
                  <span style={{ fontWeight: 600 }}>{biodata.nip}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Jenis Jabatan</span>
                  <span style={{ fontWeight: 500 }}>{biodata.jenjab?.nama}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Golongan</span>
                  <span style={{ fontWeight: 500 }}>{biodata.golruang?.nama}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>SKPD</span>
                  <span style={{ fontWeight: 500 }}>{biodata.unit?.nama}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Sub Unit</span>
                  <span style={{ fontWeight: 500 }}>{biodata.subunit?.nama}</span>
                </div>
                {biodata.eselon?.nama && (
                  <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Eselon</span>
                    <span style={{ fontWeight: 500 }}>{biodata.eselon.nama}</span>
                  </div>
                )}
              </div>

              {/* Atasan Info */}
              {skpData && (
                <div style={{
                  marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Pejabat Penilai (Atasan)
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '6px 32px' }}>
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Nama</span>
                      <span style={{ fontWeight: 600 }}>{skpData.pejabatPenilaiNama}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>NIP</span>
                      <span style={{ fontWeight: 500 }}>{skpData.pejabatPenilaiNip}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Jabatan</span>
                      <span style={{ fontWeight: 500 }}>{skpData.pejabatPenilaiJab}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 70, fontWeight: 500 }}>Periode SKP</span>
                      <span style={{ fontWeight: 500 }}>{skpData.periodeSkp}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Data pegawai tidak tersedia</p>
        )}
      </div>

      {/* Step 1: Input Commits */}
      <div className="glass-card animate-slide-up" style={{ padding: 24, marginBottom: 20, animationDelay: '0.1s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="step-indicator"><span className="step-number">1</span> Input Commits</span>
            {commitCount > 0 && (
              <span className="badge badge-info">{commitCount} commit</span>
            )}
          </div>
          <button 
            onClick={handleOpenGitModal}
            disabled={isCheckingTokens}
            className="btn-secondary" 
            style={{ padding: '8px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, opacity: isCheckingTokens ? 0.7 : 1 }}
          >
            {isCheckingTokens ? <Loader2 size={16} className="animate-spin" /> : <DownloadCloud size={16} />} 
            Import dari Git
          </button>
        </div>

        {/* Drop zone */}
        <div
          className={`drop-zone ${isDragging ? 'active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ marginBottom: 16 }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            style={{ display: 'none' }}
          />
          <div style={{ fontSize: 32, marginBottom: 8 }}><FileText size={32} /></div>
          {fileName ? (
            <p style={{ fontWeight: 600, color: 'var(--accent)' }}>{fileName}</p>
          ) : (
            <>
              <p style={{ fontWeight: 600, fontSize: 14 }}>Drop file .txt atau klik untuk upload</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Contoh: smartvillage.txt, lapormasbup.txt
              </p>
            </>
          )}
        </div>

        <label className="label">Atau paste manual</label>
        <textarea
          className="input-field"
          placeholder={`01. Add initial project setup  Created the project structure...  pada aplikasi Smart Village\n02. Implement login page  Added authentication...  pada aplikasi Smart Village`}
          value={commitLogs}
          onChange={(e) => setCommitLogs(e.target.value)}
          style={{ minHeight: 160, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }}
        />
      </div>

      {/* Step 2: Configuration */}
      <div className="glass-card animate-slide-up" style={{ padding: 24, marginBottom: 20, animationDelay: '0.2s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span className="step-indicator"><span className="step-number">2</span> Konfigurasi</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <label className="label">Nama Project (Opsional)</label>
            <input
              className="input-field"
              placeholder="Smart Village"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Tanggal Mulai (Opsional)</label>
            <input
              type="date"
              className="input-field"
              value={tanggalMulai}
              onChange={(e) => setTanggalMulai(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Tanggal Selesai (Opsional)</label>
            <input
              type="date"
              className="input-field"
              value={tanggalSelesai}
              onChange={(e) => setTanggalSelesai(e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <label className="label">Bulan Data (Referensi E-Kinerja)</label>
            <SearchableSelect
              options={BULAN_LIST.map((b) => ({ value: b, label: b }))}
              value={bulan}
              onChange={setBulan}
              placeholder="Pilih Bulan..."
            />
          </div>
          <div>
            <label className="label">Tahun Data</label>
            <SearchableSelect
              options={TAHUN_LIST.map((t) => ({ value: t, label: t }))}
              value={tahun}
              onChange={setTahun}
              placeholder="Pilih Tahun..."
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label className="label">Min / Hari</label>
            <input
              className="input-field"
              type="number"
              min="1"
              max="5"
              value={minPerHari}
              onChange={(e) => setMinPerHari(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Max / Hari</label>
            <input
              className="input-field"
              type="number"
              min="1"
              max="5"
              value={maxPerHari}
              onChange={(e) => setMaxPerHari(e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="label">
            Rencana Kinerja (dari SKP)
            {rencanaLoading && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}><Loader2 size={12} className="animate-spin" style={{ marginRight: 4, display: "inline-block", verticalAlign: "middle" }} /> Memuat...</span>}
          </label>
          {rencanaList.length > 0 ? (
            <SearchableSelect
              options={rencanaList.map(item => ({ value: item.targetSkpBkn, label: item.targetSkpBkn }))}
              value={rencana}
              onChange={setRencana}
              placeholder="Cari atau pilih rencana kinerja..."
            />
          ) : (
            <textarea
              className="input-field"
              placeholder={rencanaLoading ? 'Memuat rencana kinerja...' : 'Rencana kinerja tidak ditemukan, input manual'}
              value={rencana}
              onChange={(e) => setRencana(e.target.value)}
              style={{ minHeight: 80 }}
              disabled={rencanaLoading}
            />
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <button
            className="btn-primary"
            onClick={handleGenerate}
            disabled={isLoading}
            style={{ fontSize: 15, padding: '14px 32px' }}
          >
            {isLoading ? (
              <>
                <span style={{ animation: 'spin-slow 1s linear infinite', display: 'inline-block' }}><Sparkles size={16} className="animate-pulse" /></span>
                Generating...
              </>
            ) : (
              <><Sparkles size={16} style={{ marginRight: 6 }} /> Generate Catatan Kinerja</>
            )}
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="glass-card animate-fade-in" style={{ padding: 24, marginBottom: 20 }}>
          <LoadingSpinner />
        </div>
      )}

      {/* Step 3: Preview & Edit */}
      {entries.length > 0 && !isLoading && (
        <div className="glass-card animate-slide-up" style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="step-indicator"><span className="step-number">3</span> Preview & Edit</span>
              <span className="badge badge-success">{entries.length} entri</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn-primary"
                onClick={handleSubmitEkinerja}
                disabled={isSubmitting}
                style={{ padding: '8px 16px', fontSize: 13, background: 'var(--accent)', color: 'white', borderColor: 'var(--accent)' }}
              >
                {isSubmitting ? 'Mengirim...' : <><Send size={16} style={{ display: "inline-block", marginRight: 6 }} /> Kirim ke E-Kinerja</>}
              </button>
              <button className="btn-secondary" onClick={addEntry} style={{ padding: '8px 16px', fontSize: 13 }}>
                <Plus size={16} style={{ display: "inline-block", marginRight: 6, verticalAlign: "middle" }} /> Tambah Baris
              </button>
              <button className="btn-secondary" onClick={handleExportCSV} style={{ padding: '8px 16px', fontSize: 13 }}>
                <Download size={16} style={{ marginRight: 6 }} /> Download CSV
              </button>
            </div>
          </div>

          <div style={{ overflow: 'auto', borderRadius: 12, border: '1px solid var(--border)' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      style={{ cursor: 'pointer' }}
                      checked={entries.length > 0 && entries.every(e => e.isSelected !== false)}
                      onChange={(e) => setEntries(prev => prev.map(p => ({ ...p, isSelected: e.target.checked })))}
                    />
                  </th>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 120 }}>Tanggal</th>
                  <th style={{ width: 90 }}>Mulai</th>
                  <th style={{ width: 90 }}>Selesai</th>
                  <th style={{ minWidth: 300 }}>Uraian</th>
                  <th style={{ width: 70 }}>Jumlah</th>
                  <th style={{ width: 100 }}>Satuan</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={idx} style={{ opacity: entry.isSelected === false ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={entry.isSelected !== false}
                        onChange={(e) => updateEntry(idx, 'isSelected', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>{idx + 1}</td>
                    <td>
                      <input
                        type="date"
                        className="input-field"
                        value={entry.tanggal}
                        onChange={(e) => updateEntry(idx, 'tanggal', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 12 }}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="input-field"
                        value={entry.waktuMulai || ''}
                        onChange={(e) => updateEntry(idx, 'waktuMulai', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 12 }}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="input-field"
                        value={entry.waktuSelesai || ''}
                        onChange={(e) => updateEntry(idx, 'waktuSelesai', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 12 }}
                      />
                    </td>
                    <td>
                      <div
                        className="editable-cell"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => updateEntry(idx, 'uraian', e.currentTarget.textContent || '')}
                        dangerouslySetInnerHTML={{ __html: entry.uraian }}
                      />
                    </td>
                    <td>
                      <input
                        className="input-field"
                        value={entry.jumlah}
                        onChange={(e) => updateEntry(idx, 'jumlah', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 12, textAlign: 'center' }}
                      />
                    </td>
                    <td>
                      <input
                        className="input-field"
                        value={entry.satuan}
                        onChange={(e) => updateEntry(idx, 'satuan', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 12 }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn-danger"
                        onClick={() => deleteEntry(idx)}
                        style={{ padding: '6px 10px', fontSize: 11, borderRadius: 8 }}
                        title="Hapus"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <AlertCircle size={14} style={{ display: "inline-block", marginRight: 4, verticalAlign: "middle" }} /> Klik pada kolom uraian untuk mengedit. File CSV akan bernama <strong>{bulan}-{tahun}.csv</strong>
            </p>
            <button className="btn-primary" onClick={handleExportCSV} style={{ padding: '12px 28px' }}>
              <Download size={16} style={{ marginRight: 6 }} /> Download {bulan}-{tahun}.csv
            </button>
          </div>
        </div>
      )}

      {/* Failed Entries Table */}
      {failedEntries.length > 0 && (
        <div className="glass-card animate-slide-up" style={{ padding: 24, marginBottom: 20, border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>
                <XCircle size={14} style={{ display: "inline-block", marginRight: 4, verticalAlign: "middle" }} /> Gagal Dikirim
              </span>
              <span className="badge" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>{failedEntries.length} entri</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn-primary"
                disabled={isRetrying}
                onClick={async () => {
                  setIsRetrying(true);
                  let retrySuccess = 0;
                  const stillFailed: typeof failedEntries = [];

                  for (const failed of failedEntries) {
                    const e = failed.entry;
                    const wMulai = e.waktuMulai || '08:00';
                    const wSelesai = e.waktuSelesai || '16:00';
                    const [hM2, mM2] = wMulai.split(':').map(Number);
                    const [hS2, mS2] = wSelesai.split(':').map(Number);
                    let tMenit = (hS2 * 60 + mS2) - (hM2 * 60 + mM2);
                    if (tMenit < 0) tMenit = 0;
                    const j = Math.floor(tMenit / 60);
                    const m = tMenit % 60;
                    const wStr = j > 0 ? (m > 0 ? `${j} Jam ${m} Menit` : `${j} Jam 0 Menit`) : `${m} Menit`;

                    const payload = {
                      tanggal: e.tanggal,
                      waktuMulai: `${wMulai}:00`,
                      waktuSelesai: `${wSelesai}:00`,
                      jam: j, menit: m, waktu: wStr,
                      uraian: e.uraian, output: e.satuan, skp: 'ada',
                      nip: biodata.nip || biodata.id_pegawai,
                      foto: biodata.photo || biodata.foto,
                      nama: biodata.nama?.nama || biodata.nama,
                      jab: biodata.jab?.nama || biodata.nomenklatur_jabatan,
                      atasan: skpData?.pejabatPenilaiNip || biodata.atasan,
                      atasanNama: skpData?.pejabatPenilaiNama || biodata.atasanNama?.nama || biodata.atasanNama,
                      atasanJab: skpData?.pejabatPenilaiJab || biodata.atasanJab?.nama || biodata.atasanJab,
                      skpd: biodata.unit?.id || biodata.skpd,
                      skpdNama: biodata.unit?.nama || biodata.skpdNama,
                      subUnit: biodata.subunit?.id || biodata.subUnit,
                      jumlah: Number(e.jumlah),
                      rencanaKinerjaId: failed.rencanaKinerjaId,
                    };

                    try {
                      const res = await fetch('/api/submit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                      });
                      if (res.ok) {
                        const resData = await res.json().catch(() => null);
                        if (resData?.success === false || resData?.error) {
                          stillFailed.push({ ...failed, error: resData?.error || resData?.details?.message || 'Unknown error' });
                        } else {
                          retrySuccess++;
                        }
                      } else {
                        const errData = await res.json().catch(() => null);
                        stillFailed.push({ ...failed, error: errData?.error || errData?.details?.message || `HTTP ${res.status}` });
                      }
                    } catch {
                      stillFailed.push({ ...failed, error: 'Network error' });
                    }
                  }

                  setFailedEntries(stillFailed);
                  setIsRetrying(false);

                  if (stillFailed.length === 0) {
                    setToast({ message: `Retry berhasil! ${retrySuccess} entri terkirim`, type: 'success' });
                  } else {
                    setToast({ message: `${retrySuccess} berhasil, ${stillFailed.length} masih gagal`, type: 'error' });
                  }
                }}
                style={{ padding: '8px 16px', fontSize: 13, background: '#ef4444', borderColor: '#ef4444' }}
              >
                {isRetrying ? <><Loader2 size={14} className="animate-spin" style={{ display: "inline-block", marginRight: 4 }} /> Mengirim ulang...</> : `<RefreshCw size={14} style={{ display: "inline-block", marginRight: 4 }} /> Kirim Ulang Semua (${failedEntries.length})`}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setFailedEntries([])}
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                <X size={14} style={{ display: "inline-block", marginRight: 4 }} /> Tutup
              </button>
            </div>
          </div>

          <div style={{ overflow: 'auto', borderRadius: 12, border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 120 }}>Tanggal</th>
                  <th style={{ width: 90 }}>Mulai</th>
                  <th style={{ width: 90 }}>Selesai</th>
                  <th style={{ minWidth: 250 }}>Uraian</th>
                  <th style={{ minWidth: 200 }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {failedEntries.map((failed, idx) => (
                  <tr key={idx}>
                    <td style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>{idx + 1}</td>
                    <td style={{ fontSize: 12 }}>{failed.entry.tanggal}</td>
                    <td style={{ fontSize: 12 }}>{failed.entry.waktuMulai}</td>
                    <td style={{ fontSize: 12 }}>{failed.entry.waktuSelesai}</td>
                    <td style={{ fontSize: 12, lineHeight: 1.5 }}>{failed.entry.uraian}</td>
                    <td>
                      <span style={{
                        fontSize: 11, color: '#ef4444', background: 'rgba(239, 68, 68, 0.08)',
                        padding: '4px 8px', borderRadius: 6, display: 'inline-block', lineHeight: 1.4,
                      }}>
                        {failed.error}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="animate-fade-in" style={{ textAlign: 'center', marginTop: 40 }}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          EKIN Generator · Powered by OpenRouter AI · Made with <Heart size={12} fill="currentColor" style={{ display: "inline-block", margin: "0 2px", verticalAlign: "middle" }} />
        </p>
      </footer>

      {showMissingTokenModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div className="animate-slide-up" style={{
            backgroundColor: 'var(--bg-primary)', borderRadius: 16, width: '90%', maxWidth: 400,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: 24, border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, color: '#f59e0b' }}>
              <AlertCircle size={48} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Token Belum Dikonfigurasi</h3>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 24, lineHeight: 1.5 }}>
              Anda belum mengatur Personal Access Token (PAT) untuk GitHub atau GitLab. Silakan tambahkan token di halaman Pengaturan terlebih dahulu untuk menggunakan fitur Import dari Git.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                className="btn-secondary" 
                onClick={() => setShowMissingTokenModal(false)}
                style={{ flex: 1, padding: '10px 16px' }}
              >
                Batal
              </button>
              <a 
                href="/settings" 
                className="btn-primary" 
                style={{ flex: 1, padding: '10px 16px', textDecoration: 'none', textAlign: 'center' }}
              >
                Ke Pengaturan
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Git Import Modal */}
      <GitImportModal 
        isOpen={isGitModalOpen} 
        onClose={() => setIsGitModalOpen(false)} 
        availablePlatforms={availablePlatforms}
        onSuccess={(text) => {
          setCommitLogs(prev => prev ? prev + '\n' + text : text);
          setToast({ message: 'Berhasil mengimpor commits', type: 'success' });
        }}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
